import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { usePermissionGuard } from "@/hooks/usePermissionGuard"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  Bell,
  BellOff,
  BellRing,
  Loader2,
  MoreVertical,
  SquarePen,
  Trash2,
  XCircle,
} from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { NOTE_WRITE_PERMISSION } from "../../../api/notesApi"
import {
  INQUIRY_STATUS_DISPLAY,
  useCloseInquiry,
  useDeleteInquiry,
  useSetAutoReminders,
  useWaiveOpenRegulations,
  type InquiryStatus,
  type SupplierInquiryDetail,
} from "../../../api/supplierInquiriesApi"
import { regulationKeyLabels } from "../../../utils/regulationLabel"
import { DetailPanelHeader } from "../../shared/DetailPanelHeader"
import { McPill } from "../../shared/McPill"
import {
  IDLE_CLOSE_FLOW,
  InquiryCloseNoteForm,
  type CloseFlow,
} from "./InquiryCloseNoteForm"
import { InquiryWaiverForm } from "./InquiryWaiverForm"
import { ReplyPendingPill, replyPendingKind } from "./ReplyPendingPill"
import { inquiryDraftPath } from "./routes"

// A reminder only makes sense once the inquiry has been sent and is awaiting a
// response. Not for draft (not sent yet), under-review, or answered (closed).
const REMINDABLE_STATUSES: ReadonlySet<InquiryStatus> = new Set<InquiryStatus>([
  "waiting",
  "reminded",
  "escalated",
])

interface InquiryDetailHeaderProps {
  inquiry: SupplierInquiryDetail
  /** The draft is gone from the list — deselect it. */
  onDiscarded: () => void
}

/** Status pill plus every per-inquiry action, with their confirmation dialogs. */
export function InquiryDetailHeader({
  inquiry,
  onDiscarded,
}: InquiryDetailHeaderProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  // Writing the note takes the send permission. Without it the dialog keeps the
  // plain confirm, so no reason is offered that the server would refuse.
  const canWriteNotes = usePermissionGuard(NOTE_WRITE_PERMISSION.inquiry).canRun
  const closeWithNote = useFeatureFlag("MC_NOTES") && canWriteNotes
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)
  // The close dialog's choice when something is still open: close the request, or
  // close it as answered with the rest waived (TUR-1732).
  const [waiving, setWaiving] = useState(false)
  // Kept here, not in the dialog: its content unmounts on every dismiss, and a
  // reopen after a failed close must show the same draft and reuse the notes
  // already written. Tied to the inquiry it was written for: when another one
  // is shown the dialog closes and the flow starts over, and a write still in
  // flight for the old inquiry is dropped, so nothing is ever sent for the
  // wrong one.
  const [closeState, setCloseState] = useState({
    inquiryId: inquiry.id,
    flow: IDLE_CLOSE_FLOW,
  })
  if (closeState.inquiryId !== inquiry.id) {
    setCloseState({ inquiryId: inquiry.id, flow: IDLE_CLOSE_FLOW })
    setConfirmCloseOpen(false)
    setWaiving(false)
  }
  const closeFlow = closeState.flow
  const inquiryId = inquiry.id
  const changeCloseFlow = (patch: Partial<CloseFlow>) =>
    setCloseState((prev) =>
      prev.inquiryId === inquiryId
        ? { ...prev, flow: { ...prev.flow, ...patch } }
        : prev,
    )
  // The inquiry on screen right now, for a callback that outlives its render:
  // a close that lands after another inquiry is shown.
  const shownInquiryId = useRef(inquiry.id)
  useEffect(() => {
    shownInquiryId.current = inquiry.id
  }, [inquiry.id])
  // A dismiss with nothing written abandons the draft, so the next open starts
  // empty. Once a note is saved the draft stays: the retry must reuse that note.
  const dismissClose = () => {
    setConfirmCloseOpen(false)
    setWaiving(false)
    if (
      closeFlow !== IDLE_CLOSE_FLOW &&
      closeFlow.inquiryNoteId === null &&
      !closeFlow.supplierNoteWritten
    )
      changeCloseFlow(IDLE_CLOSE_FLOW)
  }

  const closeInquiry = useCloseInquiry()
  const waive = useWaiveOpenRegulations()
  const deleteInquiry = useDeleteInquiry()
  const setAutoReminders = useSetAutoReminders()

  const display = INQUIRY_STATUS_DISPLAY[inquiry.status]
  const pendingKind = replyPendingKind(inquiry)
  const isDraft = inquiry.status === "draft"
  const canRemind = REMINDABLE_STATUSES.has(inquiry.status)
  // Auto-reminders actively cycle only while the inquiry is awaiting a response, so the
  // pause/resume control is offered there (not on draft / escalated / terminal). A paused
  // inquiry stays 'waiting', so it stays controllable.
  const isRemindableStatus =
    inquiry.status === "waiting" || inquiry.status === "reminded"
  const remindersPaused = inquiry.autoRemindersEnabled === false
  // Show "Pause" when reminders are effectively running, and "Resume" when this inquiry is
  // explicitly paused. When org reminders are off and this inquiry merely inherits (nothing
  // running, not paused), there's nothing to pause — and force-on isn't surfaced here — so
  // the control is hidden. Resume clears the override back to inherit (follow the org), so it
  // never pins force-on.
  const showReminderToggle =
    isRemindableStatus && (remindersPaused || inquiry.autoRemindersEffective)
  // A draft was never sent — it's discarded (deleted), not "closed". Close is the
  // deliberate stop for an already-sent inquiry; terminal inquiries (answered / closed)
  // can't be closed again. So Close and Discard are mutually exclusive per status.
  const canDiscard = isDraft
  const canClose =
    !isDraft && inquiry.status !== "answered" && inquiry.status !== "closed"
  // A partial answer left regulations open: they can be waived on close, by whoever
  // may send (the waiver mutation's own guard).
  const canWaive = canClose && !!inquiry.openRegulations?.length && waive.canRun

  const draftHref = (stage?: "reminder") => inquiryDraftPath(inquiry.id, stage)

  const handleClose = () => {
    closeInquiry.mutate(
      { id: inquiry.id },
      { onSuccess: () => setConfirmCloseOpen(false) },
    )
  }

  // With notes on, a dismiss while the notes upload or the close is in flight
  // would hide the dialog and still close the inquiry behind it.
  const closeBusy =
    (closeWithNote && (closeFlow.writing || closeInquiry.isPending)) ||
    waive.isPending

  const handleDiscard = () => {
    deleteInquiry.mutate(inquiry.id, {
      onSuccess: () => {
        setConfirmDiscardOpen(false)
        onDiscarded()
      },
    })
  }

  return (
    <>
      <DetailPanelHeader
        label={translate("MC_INQ_DETAIL_LABEL")}
        title={inquiry.supplierName ?? "—"}
        subtitle={inquiry.reference ?? "—"}
        actions={({ iconOnly }) => (
          <>
            {pendingKind && <ReplyPendingPill kind={pendingKind} />}
            {display && (
              <McPill tone={display.tone}>{translate(display.labelKey)}</McPill>
            )}
            {isDraft && (
              <Button
                size="sm"
                className="h-[30px] gap-1.5 bg-yellow1 text-[12.5px] text-black hover:bg-yellow2"
                aria-label={translate("MC_INQ_OPEN_DRAFT")}
                title={translate("MC_INQ_OPEN_DRAFT")}
                onClick={() => navigate(draftHref())}
              >
                <SquarePen size={13} />
                {!iconOnly && translate("MC_INQ_OPEN_DRAFT")}
              </Button>
            )}
            {canRemind && (
              <Button
                size="sm"
                variant="outline"
                className="h-[30px] gap-1.5 text-[12.5px]"
                aria-label={translate("MC_INQ_REMIND")}
                title={translate("MC_INQ_REMIND")}
                onClick={() => navigate(draftHref("reminder"))}
              >
                <Bell size={13} />
                {!iconOnly && translate("MC_INQ_REMIND")}
              </Button>
            )}
            {showReminderToggle && (
              <Button
                size="sm"
                variant="outline"
                className="h-[30px] gap-1.5 text-[12.5px]"
                disabled={setAutoReminders.isPending}
                onClick={() =>
                  setAutoReminders.mutate({
                    id: inquiry.id,
                    // Paused → resume by clearing the override back to inherit (follow org);
                    // running → pause this inquiry. Never force-on from here.
                    mode: remindersPaused ? "inherit" : "off",
                  })
                }
                aria-label={translate(
                  remindersPaused
                    ? "MC_INQ_RESUME_REMINDERS"
                    : "MC_INQ_PAUSE_REMINDERS",
                )}
                title={translate(
                  remindersPaused
                    ? "MC_INQ_RESUME_REMINDERS_HINT"
                    : "MC_INQ_PAUSE_REMINDERS_HINT",
                )}
              >
                {setAutoReminders.isPending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : remindersPaused ? (
                  <BellRing size={13} />
                ) : (
                  <BellOff size={13} />
                )}
                {!iconOnly &&
                  translate(
                    remindersPaused
                      ? "MC_INQ_RESUME_REMINDERS"
                      : "MC_INQ_PAUSE_REMINDERS",
                  )}
              </Button>
            )}
            {(canClose || canDiscard) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-[30px] w-[30px]"
                    aria-label={translate("MC_INQ_MORE_ACTIONS")}
                  >
                    <MoreVertical size={15} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canClose && (
                    <DropdownMenuItem
                      className="text-red1 focus:text-red1"
                      onSelect={() => setConfirmCloseOpen(true)}
                    >
                      <XCircle size={14} />
                      {translate("MC_INQ_CLOSE_REQUEST")}
                    </DropdownMenuItem>
                  )}
                  {canDiscard && (
                    <DropdownMenuItem
                      className="text-red1 focus:text-red1"
                      onSelect={() => setConfirmDiscardOpen(true)}
                    >
                      <Trash2 size={14} />
                      {translate("MC_INQ_DISCARD")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        )}
      />

      <Dialog
        open={confirmCloseOpen}
        onOpenChange={(open) => {
          if (!open && closeBusy) return
          if (open) setConfirmCloseOpen(true)
          else dismissClose()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate("MC_INQ_CLOSE_CONFIRM_TITLE")}</DialogTitle>
            <DialogDescription>
              {translate("MC_INQ_CLOSE_CONFIRM_DESC")}
            </DialogDescription>
          </DialogHeader>
          {canWaive && (
            <RadioGroup
              value={waiving ? "waive" : "close"}
              onValueChange={(value) => setWaiving(value === "waive")}
              // Switching mid-submission would unmount the form whose request is
              // still in flight, and offer a second, competing one.
              disabled={
                closeFlow.writing || closeInquiry.isPending || waive.isPending
              }
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="close" id="inquiry-close-mode-close" />
                <Label
                  htmlFor="inquiry-close-mode-close"
                  className="font-normal"
                >
                  {translate("MC_INQ_CLOSE_MODE_CLOSE")}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="waive" id="inquiry-close-mode-waive" />
                <Label
                  htmlFor="inquiry-close-mode-waive"
                  className="font-normal"
                >
                  {translate("MC_INQ_CLOSE_MODE_WAIVE", {
                    list: regulationKeyLabels(
                      inquiry.openRegulations ?? [],
                      translate,
                    ),
                  })}
                </Label>
              </div>
            </RadioGroup>
          )}
          {canWaive && waiving ? (
            <InquiryWaiverForm
              inquiry={inquiry}
              waive={waive}
              onDone={() => {
                // A waiver that lands for an inquiry no longer shown must not
                // shut the dialog that is open for the current one.
                if (shownInquiryId.current !== inquiryId) return
                setConfirmCloseOpen(false)
                setWaiving(false)
              }}
              onCancel={dismissClose}
            />
          ) : closeWithNote ? (
            <InquiryCloseNoteForm
              key={inquiry.id}
              inquiry={inquiry}
              closeInquiry={closeInquiry}
              flow={closeFlow}
              onFlowChange={changeCloseFlow}
              onDone={() => {
                // A close that lands for an inquiry no longer shown must not
                // shut the dialog that is open for the current one.
                if (shownInquiryId.current !== inquiryId) return
                setConfirmCloseOpen(false)
                changeCloseFlow(IDLE_CLOSE_FLOW)
              }}
              onCancel={dismissClose}
            />
          ) : (
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmCloseOpen(false)}
                disabled={closeInquiry.isPending}
              >
                {translate("MC_DECL_BTN_CANCEL")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleClose}
                disabled={closeInquiry.isPending}
              >
                {closeInquiry.isPending && (
                  <Loader2 size={14} className="animate-spin" />
                )}
                {translate("MC_INQ_CLOSE_REQUEST")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate("MC_INQ_DISCARD_CONFIRM_TITLE")}
            </DialogTitle>
            <DialogDescription>
              {translate("MC_INQ_DISCARD_CONFIRM_DESC")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDiscardOpen(false)}
              disabled={deleteInquiry.isPending}
            >
              {translate("MC_DECL_BTN_CANCEL")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDiscard}
              disabled={deleteInquiry.isPending}
            >
              {deleteInquiry.isPending && (
                <Loader2 size={14} className="animate-spin" />
              )}
              {translate("MC_INQ_DISCARD")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
